// Partner Dashboard Configuration
// This file manages which steps and features are enabled/disabled
// Steps are organized as a linked list for easy reordering, insertion, and removal

// ============================================================================
// LINKED LIST STRUCTURE - HOW TO USE
// ============================================================================
// Each step has: key (unique identifier), next (key of next step), prev (key of previous step)
//
// TO ADD A NEW STEP:
//   1. Add the step object to the steps object
//   2. Update next/prev pointers to insert it in the chain
//   3. Example: To add 'newStep' after 'meeting':
//      - newStep: { key: 'newStep', next: 'partnership', prev: 'meeting', ... }
//      - meeting.next = 'newStep'
//      - partnership.prev = 'newStep'
//
// TO REMOVE A STEP:
//   1. Use ConfigHelpers.removeStep('stepKey') OR
//   2. Manually update pointers:
//      - prevStep.next = step.next
//      - nextStep.prev = step.prev
//      - Delete the step object
//
// TO REORDER STEPS:
//   1. Use ConfigHelpers.moveStep('stepKey', 'newPositionKey', insertAfter) OR
//   2. Manually update the next/prev chain
//
// TO DISABLE A STEP (keep in chain but hide):
//   - Set step.enabled = false (step remains in chain but won't show in enabled steps)
//
// ============================================================================

export const PARTNER_CONFIG = {
  // Global settings
  global: {
    enableDebugMode: process.env.REACT_APP_ENABLE_DEBUG === 'true',
    enableFeatureFlags: true,
    enableStepCustomization: true,
  },

  // Linked list head - points to the first step
  stepHead: 'nda',

  // Step configurations (linked list structure)
  // Each step has: key (unique identifier), next (key of next step), prev (key of previous step)
  steps: {
    // Step 1: NDA (head of the list)
    nda: {
      key: 'nda',
      next: 'meeting',  // Points to next step
      prev: null,       // First step has no previous
      title: "NDA",
      description: "Non-Disclosure Agreement",
      enabled: false,
      required: false,
      prerequisites: [], // Prerequisites are now step keys (empty array means no prerequisites)
      features: {
        agreementSigning: true,
        documentViewing: true,
        termsAcceptance: true,
      }
    },
    // Step 2: Setup a Meeting
    meeting: {
      key: 'meeting',
      next: 'partnership',
      prev: 'nda',
      title: "Setup a Meeting",
      description: "Schedule a meeting to discuss partnership",
      enabled: false,
      required: false,
      prerequisites: ['nda'], // References step by key
      features: {
        meetingScheduling: true,
        calendarIntegration: false, // Will be enabled later
        emailNotification: true,
      }
    },
    // Step 3: Partnership Agreement
    partnership: {
      key: 'partnership',
      next: 'productDevelopment',
      prev: null,
      title: "Partnership Agreement",
      description: "Partnership and Service Agreement",
      enabled: true,
      required: true,
      prerequisites: ['meeting'],
      features: {
        agreementSigning: true,
        documentViewing: true,
        termsAcceptance: true,
      }
    },
    // Step 4: Product Development
    productDevelopment: {
      key: 'productDevelopment',
      next: 'legal',
      prev: 'partnership',
      title: "Product Development",
      description: "Product Development Templates and Resources",
      enabled: true,
      required: true,
      prerequisites: ['partnership'],
      features: {
        trainingModules: true,
        resourceAccess: true,
        certificationTracking: true,
      },
      subTasks: {
        islamicFinance101: true,
        salesTraining: true,
        systemTraining: true,
        resourceAccess: true,
      }
    },
    // Step 6: Legal Compliance (currently disabled)
    legal: {
      key: 'legal',
      next: 'businessDocumentation', // Points to business documentation
      prev: 'productDevelopment',
      title: "Legal Compliance",
      enabled: false,
      required: false,
      description: "Regulatory compliance verification",
      prerequisites: ['productDevelopment'],
      features: {
        documentUpload: true,
        complianceReview: true,
        regulatoryReporting: true,
      },
      subTasks: {
        regulatoryLicenses: true,
        amlProcedures: true,
        kycProtocols: true,
        complianceReporting: true,
      }
    },
    // Step 5: Business Documentation
    businessDocumentation: {
      key: 'businessDocumentation',
      next: 'shariyah',
      prev: 'productDevelopment',
      title: "Business Documentation",
      description: "Upload confidential business documents",
      enabled: true,
      required: true,
      prerequisites: ['productDevelopment'],
      features: {
        documentUpload: true,
        confidentialStorage: true,
        businessReview: true,
      },
      subTasks: {
        riskManagement: true,
        creditProcess: true,
        financialFlow: true,
        operationalModel: true,
      }
    },
    // Step 6: Shariyah Certification
    shariyah: {
      key: 'shariyah',
      next: null, // Points to legal (even though it's disabled)
      prev: 'businessDocumentation',
      title: "Shariah Certification",
      description: "Manage Issued Shariyah Certifications",
      enabled: true,
      required: true,
      prerequisites: ['businessDocumentation'],
      features: {
        documentUpload: true,
        advisorReview: true,
        certificationIssuance: true,
        partnerTypeSelection: true,
      },
      subTasks: {
        documentRequirements: true,
        advisorCommunication: true,
        certificationProcess: true,
      }
    },
    // Step 7: Tech Setup
    techSetup: {
      key: 'techSetup',
      next: 'launch',
      prev: 'shariyah', // Points back to legal (previous step in chain)
      title: "Tech Setup",
      description: "Halal finance technology infrastructure",
      enabled: false,
      required: false,
      prerequisites: ['shariyah'], // Can have multiple prerequisites
      features: {
        systemIntegration: true,
        apiSetup: true,
        testingEnvironment: true,
      },
      subTasks: {
        loanApplicationSystem: true,
        creditScoring: true,
        paymentProcessing: true,
        auditTrail: false, // Islamic Audit Trail - disabled for demonstration
        customerPortal: true,
      }
    },
    // Step 8: Launch & Community Engagement (tail of the list)
    launch: {
      key: 'launch',
      next: null,  // Last step has no next
      prev: 'techSetup',
      title: "Launch & Community Engagement",
      description: "Community outreach and service launch",
      enabled: false,
      required: false,
      prerequisites: ['shariyah'],
      features: {
        pilotProgram: true,
        pricingStrategy: true,
        transparencyTools: true,
        communityOutreach: true,
      },
      subTasks: {
        pilotProgram: true,
        pricingStrategy: true,
        transparencyTools: true,
        communityOutreach: true,
      }
    }
  },

  // Feature flags for specific functionality
  features: {
    fileUpload: {
      enabled: true,
      maxFileSize: 10 * 1024 * 1024, // 10MB
      allowedTypes: ['pdf', 'doc', 'docx', 'rtf', 'xls', 'xlsx', 'odt', 'ods', 'txt', 'csv', 'jpg', 'jpeg', 'png'],
      s3Integration: true,
    },
    progressTracking: {
      enabled: true,
      autoSave: true,
      localStorage: true,
      apiSync: false, // Will be enabled when API is ready
    },
    notifications: {
      enabled: true,
      email: false,
      inApp: true,
      reminders: true,
    },
    analytics: {
      enabled: false,
      tracking: false,
      reporting: false,
    }
  },

  // Partner type specific configurations
  partnerTypes: {
    existing: {
      enabled: true,
      customSteps: [],
      skipSteps: [],
      additionalRequirements: ['existing_certification', 'audit_reports'],
    },
    new: {
      enabled: true,
      customSteps: [],
      skipSteps: [],
      additionalRequirements: ['concept_paper', 'draft_agreements'],
    }
  }
};

// Helper functions to check configuration and manage linked list
export const ConfigHelpers = {
  // ==========================================
  // Linked List Traversal Functions
  // ==========================================

  // Get all steps in order (traverse the linked list)
  getAllStepsInOrder: () => {
    const steps = [];
    let currentKey = PARTNER_CONFIG.stepHead;
    const visited = new Set();

    while (currentKey && !visited.has(currentKey)) {
      visited.add(currentKey);
      const step = PARTNER_CONFIG.steps[currentKey];
      if (step) {
        steps.push(step);
        currentKey = step.next;
      } else {
        break;
      }
    }

    return steps;
  },

  // Get enabled steps in order
  getEnabledSteps: () => {
    return ConfigHelpers.getAllStepsInOrder().filter(step => step.enabled);
  },

  // Get step by key
  getStepByKey: (stepKey) => {
    return PARTNER_CONFIG.steps[stepKey] || null;
  },

  // Get step by numeric ID (for backward compatibility)
  // Numeric ID is the position in the ordered list
  getStepById: (stepId) => {
    const steps = ConfigHelpers.getAllStepsInOrder();
    return steps[stepId - 1] || null; // IDs are 1-indexed
  },

  // Get numeric ID for a step key (position in ordered list)
  getStepId: (stepKey) => {
    const steps = ConfigHelpers.getAllStepsInOrder();
    const index = steps.findIndex(step => step.key === stepKey);
    return index >= 0 ? index + 1 : null; // 1-indexed
  },

  // Get next step in chain
  getNextStep: (stepKey) => {
    const step = PARTNER_CONFIG.steps[stepKey];
    return step && step.next ? PARTNER_CONFIG.steps[step.next] : null;
  },

  // Get previous step in chain
  getPreviousStep: (stepKey) => {
    const step = PARTNER_CONFIG.steps[stepKey];
    return step && step.prev ? PARTNER_CONFIG.steps[step.prev] : null;
  },

  // ==========================================
  // Step Manipulation Functions (Linked List Operations)
  // ==========================================

  // Insert a new step after a given step
  insertStepAfter: (afterKey, newStep) => {
    const afterStep = PARTNER_CONFIG.steps[afterKey];
    if (!afterStep) {
      console.error(`Step with key "${afterKey}" not found`);
      return false;
    }

    // Add the new step
    PARTNER_CONFIG.steps[newStep.key] = {
      ...newStep,
      next: afterStep.next,
      prev: afterKey,
    };

    // Update the after step's next pointer
    afterStep.next = newStep.key;

    // Update the step that was after (if it exists)
    if (newStep.next) {
      const nextStep = PARTNER_CONFIG.steps[newStep.next];
      if (nextStep) {
        nextStep.prev = newStep.key;
      }
    }

    return true;
  },

  // Insert a new step before a given step
  insertStepBefore: (beforeKey, newStep) => {
    const beforeStep = PARTNER_CONFIG.steps[beforeKey];
    if (!beforeStep) {
      console.error(`Step with key "${beforeKey}" not found`);
      return false;
    }

    // If inserting before the head, update head
    if (beforeStep.prev === null) {
      PARTNER_CONFIG.stepHead = newStep.key;
    } else {
      // Update the previous step's next pointer
      const prevStep = PARTNER_CONFIG.steps[beforeStep.prev];
      if (prevStep) {
        prevStep.next = newStep.key;
      }
    }

    // Add the new step
    PARTNER_CONFIG.steps[newStep.key] = {
      ...newStep,
      next: beforeKey,
      prev: beforeStep.prev,
    };

    // Update the before step's prev pointer
    beforeStep.prev = newStep.key;

    return true;
  },

  // Remove a step from the chain
  removeStep: (stepKey) => {
    const step = PARTNER_CONFIG.steps[stepKey];
    if (!step) {
      console.error(`Step with key "${stepKey}" not found`);
      return false;
    }

    // Update previous step's next pointer
    if (step.prev) {
      const prevStep = PARTNER_CONFIG.steps[step.prev];
      if (prevStep) {
        prevStep.next = step.next;
      }
    } else {
      // If removing the head, update head
      PARTNER_CONFIG.stepHead = step.next;
    }

    // Update next step's prev pointer
    if (step.next) {
      const nextStep = PARTNER_CONFIG.steps[step.next];
      if (nextStep) {
        nextStep.prev = step.prev;
      }
    }

    // Delete the step
    delete PARTNER_CONFIG.steps[stepKey];
    return true;
  },

  // Move a step to a new position (reorder)
  moveStep: (stepKey, newPositionKey, insertAfter = true) => {
    const step = PARTNER_CONFIG.steps[stepKey];
    if (!step) {
      console.error(`Step with key "${stepKey}" not found`);
      return false;
    }

    // First, remove the step from its current position
    if (step.prev) {
      PARTNER_CONFIG.steps[step.prev].next = step.next;
    } else {
      PARTNER_CONFIG.stepHead = step.next;
    }

    if (step.next) {
      PARTNER_CONFIG.steps[step.next].prev = step.prev;
    }

    // Then insert it at the new position
    if (insertAfter) {
      return ConfigHelpers.insertStepAfter(newPositionKey, step);
    } else {
      return ConfigHelpers.insertStepBefore(newPositionKey, step);
    }
  },

  // ==========================================
  // Step Property Checks
  // ==========================================

  // Check if a step is enabled (by key or numeric ID)
  isStepEnabled: (stepIdOrKey) => {
    const step = typeof stepIdOrKey === 'number' 
      ? ConfigHelpers.getStepById(stepIdOrKey)
      : ConfigHelpers.getStepByKey(stepIdOrKey);
    return step && step.enabled;
  },

  // Check if a step is required (by key or numeric ID)
  isStepRequired: (stepIdOrKey) => {
    const step = typeof stepIdOrKey === 'number'
      ? ConfigHelpers.getStepById(stepIdOrKey)
      : ConfigHelpers.getStepByKey(stepIdOrKey);
    return step && step.required;
  },

  // Get step prerequisites (by key or numeric ID)
  getStepPrerequisites: (stepIdOrKey) => {
    const step = typeof stepIdOrKey === 'number'
      ? ConfigHelpers.getStepById(stepIdOrKey)
      : ConfigHelpers.getStepByKey(stepIdOrKey);
    return step ? step.prerequisites : [];
  },

  // ==========================================
  // Feature and Sub-task Checks
  // ==========================================

  // Check if a feature is enabled
  isFeatureEnabled: (featurePath) => {
    const path = featurePath.split('.');
    let current = PARTNER_CONFIG.features;
    
    for (const key of path) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key];
      } else {
        return false;
      }
    }
    
    return current && current.enabled !== false;
  },

  // Check if a sub-task is enabled (by key or numeric ID)
  isSubTaskEnabled: (stepIdOrKey, subTaskKey) => {
    const step = typeof stepIdOrKey === 'number'
      ? ConfigHelpers.getStepById(stepIdOrKey)
      : ConfigHelpers.getStepByKey(stepIdOrKey);
    return step && step.subTasks && step.subTasks[subTaskKey] === true;
  },

  // Get all enabled sub-tasks for a step (by key or numeric ID)
  getEnabledSubTasks: (stepIdOrKey) => {
    const step = typeof stepIdOrKey === 'number'
      ? ConfigHelpers.getStepById(stepIdOrKey)
      : ConfigHelpers.getStepByKey(stepIdOrKey);
    if (!step || !step.subTasks) return [];
    
    return Object.entries(step.subTasks)
      .filter(([key, enabled]) => enabled)
      .map(([key]) => key);
  },

  // ==========================================
  // Partner Type Checks
  // ==========================================

  // Check if partner type is enabled
  isPartnerTypeEnabled: (partnerType) => {
    return PARTNER_CONFIG.partnerTypes[partnerType]?.enabled === true;
  },

  // ==========================================
  // Configuration Access
  // ==========================================

  // Get configuration for a specific step (by key or numeric ID)
  getStepConfig: (stepIdOrKey) => {
    return typeof stepIdOrKey === 'number'
      ? ConfigHelpers.getStepById(stepIdOrKey)
      : ConfigHelpers.getStepByKey(stepIdOrKey);
  },

  // Update configuration (for admin use)
  updateConfig: (path, value) => {
    const pathArray = path.split('.');
    let current = PARTNER_CONFIG;
    
    for (let i = 0; i < pathArray.length - 1; i++) {
      current = current[pathArray[i]];
    }
    
    current[pathArray[pathArray.length - 1]] = value;
  }
};

export default PARTNER_CONFIG;

// ============================================================================
// USAGE EXAMPLES
// ============================================================================
//
// Example 1: Add a new step after 'meeting'
// ----------------------------------------------------------------------------
// const newStep = {
//   key: 'backgroundCheck',
//   next: null, // Will be set automatically
//   prev: null, // Will be set automatically
//   title: "Background Check",
//   description: "Partner background verification",
//   enabled: true,
//   required: true,
//   prerequisites: ['meeting'],
//   features: { verification: true }
// };
// ConfigHelpers.insertStepAfter('meeting', newStep);
//
// Example 2: Remove a step
// ----------------------------------------------------------------------------
// ConfigHelpers.removeStep('legal');
//
// Example 3: Reorder - Move 'meeting' to after 'partnership'
// ----------------------------------------------------------------------------
// ConfigHelpers.moveStep('meeting', 'partnership', true); // true = insert after
//
// Example 4: Get all steps in order
// ----------------------------------------------------------------------------
// const allSteps = ConfigHelpers.getAllStepsInOrder();
// const enabledSteps = ConfigHelpers.getEnabledSteps();
//
// Example 5: Traverse the chain
// ----------------------------------------------------------------------------
// const firstStep = PARTNER_CONFIG.steps[PARTNER_CONFIG.stepHead];
// const secondStep = ConfigHelpers.getNextStep(firstStep.key);
// const backToFirst = ConfigHelpers.getPreviousStep(secondStep.key);
// ============================================================================ 